import {
  ArrowRight,
  BadgeCheck,
  BrainCircuit,
  Gauge,
  HardDrive,
  PackageSearch,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  BusinessCenterIntelligencePayload,
  BusinessCenterIntelligenceWidgetDescriptor,
  BusinessCenterMarketplaceCard,
  BusinessCenterRecommendationCard,
} from "../../lib/types";
import { formatBytes } from "../../lib/format";

export function IntelligenceWidgetGrid({
  payload,
  mode,
}: {
  payload: BusinessCenterIntelligencePayload;
  mode: "admin" | "client";
}) {
  const widgets = payload.widgets.filter((widget) => widget.visible).sort((left, right) => left.order - right.order);
  const isClient = mode === "client";

  return (
    <div className="grid gap-4">
      <section className="flex flex-wrap gap-2">
        {widgets.map((widget) => (
          <WidgetChip key={widget.key} widget={widget} />
        ))}
      </section>

      <section className="grid gap-4 xl:grid-cols-12">
        <article className="ivory-card rounded-[1.5rem] p-6 xl:col-span-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Business Health</p>
              <h2 className="mt-3 font-display text-4xl font-bold text-ktd-rich">{payload.health.platformHealth}</h2>
              <p className="mt-3 max-w-2xl text-sm font-semibold leading-7 text-ktd-muted">
                Intelligence is being surfaced from the shared AI layer, subscription layer, and marketplace registry so
                teams can act on what the platform already knows.
              </p>
            </div>
            <div className="rounded-3xl bg-ktd-black p-4 text-white shadow-luxe">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-ktd-softGold">Score</p>
              <p className="mt-2 font-display text-6xl font-bold text-white">{payload.health.businessHealthScore}</p>
              <p className="mt-2 text-sm font-semibold text-white/68">Platform-wide health signal</p>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Workspace Health" value={payload.health.workspaceHealthScore} note="Scope-aware delivery posture" />
            <MetricCard label="Growth Score" value={payload.health.growthScore} note="Adoption and expansion signal" />
            <MetricCard label="Efficiency" value={payload.health.efficiencyScore} note="Operational throughput" />
            <MetricCard label="Adoption" value={payload.health.adoptionScore} note="Platform usage coverage" />
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <HighlightCard icon={TrendingUp} label="Revenue Trend" value={payload.health.revenueTrend === "up" ? "Trending up" : payload.health.revenueTrend === "down" ? "Trending down" : "Stable"} note={`${payload.health.projectsAtRisk} projects at risk`} />
            <HighlightCard icon={Users} label="Follow-up" value={`${payload.health.clientsRequiringFollowUp}`} note="Clients requiring attention" />
            <HighlightCard icon={BrainCircuit} label="AI Surface" value={`${widgets.length}`} note="Visible dashboard widgets" />
          </div>
        </article>

        <article className="rounded-[1.5rem] bg-ktd-black p-6 text-white shadow-luxe xl:col-span-5">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-ktd-softGold">Admin Summary</p>
          <h2 className="mt-3 font-display text-3xl font-bold text-white">{isClient ? "Client intelligence overview" : "Platform intelligence overview"}</h2>
          <div className="mt-6 grid gap-3">
            <InfoRow label="Platform health" value={payload.platform.platformHealth} />
            <InfoRow label="Product adoption" value={`${payload.platform.productAdoption} active products`} />
            <InfoRow label="Marketplace activity" value={`${payload.platform.marketplaceActivity} active signals`} />
            <InfoRow label="AI trend" value={payload.platform.aiRecommendationTrend} />
            <InfoRow label="Subscriptions" value={payload.platform.subscriptionGrowth} />
            <InfoRow label="MMV usage" value={payload.platform.mmvUsage} />
          </div>
        </article>
      </section>

      <section className="grid gap-4 xl:grid-cols-12">
        <IntelligencePanel
          title="AI Recommendations"
          eyebrow="Actionable guidance"
          description="Recommendations are surfaced from the shared AI layer and remain read-only until a user chooses to act."
          icon={Sparkles}
          className="xl:col-span-6"
        >
          <div className="grid gap-3">
            {payload.recommendations.slice(0, 3).map((item) => (
              <RecommendationCard key={item.key} recommendation={item} />
            ))}
          </div>
        </IntelligencePanel>

        <IntelligencePanel
          title="Marketplace Recommendations"
          eyebrow="Solutions and installs"
          description="The marketplace widget is powered by existing product catalog data and AI recommendations."
          icon={Store}
          className="xl:col-span-6"
        >
          <div className="grid gap-3">
            <MarketplaceGroup label="Recommended solutions" cards={payload.marketplace.recommendedSolutions} />
            <MarketplaceGroup label="Installed products" cards={payload.marketplace.installedProducts} />
            <MarketplaceGroup label="AI assistants" cards={payload.marketplace.aiAssistants} />
          </div>
        </IntelligencePanel>
      </section>

      <section className="grid gap-4 xl:grid-cols-12">
        <IntelligencePanel
          title="Workspace Intelligence"
          eyebrow="Trends and usage"
          description="A platform-level view of growth, activity, and engagement across the current scope."
          icon={Gauge}
          className="xl:col-span-4"
        >
          <div className="grid gap-3">
            <DataLine label="Growth" value={payload.workspaceInsights.workspaceGrowth} />
            <DataLine label="Usage" value={payload.workspaceInsights.usageTrend} />
            <DataLine label="Activity" value={payload.workspaceInsights.activityTrend} />
            <DataLine label="Engagement" value={payload.workspaceInsights.engagementTrend} />
            <div className="grid grid-cols-3 gap-3 pt-2">
              <MiniMetric label="Workspaces" value={payload.workspaceInsights.activeWorkspaces} />
              <MiniMetric label="Products" value={payload.workspaceInsights.activeProducts} />
              <MiniMetric label="Subs" value={payload.workspaceInsights.activeSubscriptions} />
            </div>
          </div>
        </IntelligencePanel>

        <IntelligencePanel
          title="Project Intelligence"
          eyebrow="Delivery and risk"
          description="Project signals are pulled from the conversion engine and AI scoring layer."
          icon={BadgeCheck}
          className="xl:col-span-4"
        >
          <div className="grid gap-3">
            <MiniMetric label="Project health" value={payload.projectIntelligence.projectHealthScore} />
            <DataLine label="Delay risks" value={`${payload.projectIntelligence.delayRisks} flagged`} />
            <DataLine label="Forecast" value={payload.projectIntelligence.completionForecast} />
            <DataLine label="Milestones" value={payload.projectIntelligence.milestoneProgress.join(" · ")} />
            <DataLine label="Deliverables" value={payload.projectIntelligence.deliverableStatus.join(" · ")} />
          </div>
        </IntelligencePanel>

        <IntelligencePanel
          title="Memory Media Vault"
          eyebrow="Archive and preservation"
          description="Storage, archive readiness, and preservation signals for the MMV commercial layer."
          icon={HardDrive}
          className="xl:col-span-4"
        >
          <div className="grid gap-3">
            <DataLine label="Archive readiness" value={payload.memoryMediaVault.archiveReadiness} />
            <DataLine label="Preservation candidates" value={`${payload.memoryMediaVault.preservationCandidates}`} />
            <StorageBar used={payload.memoryMediaVault.storageUsageBytes} limit={payload.memoryMediaVault.storageLimitBytes} />
            {payload.memoryMediaVault.upgradeRecommendations.map((item) => (
              <div key={item} className="rounded-2xl border border-ktd-gold/10 bg-white p-4 text-sm font-semibold leading-6 text-ktd-muted">
                {item}
              </div>
            ))}
          </div>
        </IntelligencePanel>
      </section>

      <section className="grid gap-4 xl:grid-cols-12">
        <IntelligencePanel
          title="Subscription Intelligence"
          eyebrow="Plan and capacity"
          description="Plan pressure, capacity, and upgrade prompts from the subscription layer."
          icon={PackageSearch}
          className="xl:col-span-6"
        >
          <div className="grid gap-3">
            <div className="rounded-2xl bg-ktd-black p-4 text-white">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-ktd-softGold">Current plan</p>
              <p className="mt-2 font-display text-3xl font-bold">{payload.subscription.currentPlan}</p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <ListBox title="Usage" items={payload.subscription.usageSummary} />
              <ListBox title="Capacity" items={payload.subscription.remainingCapacity} />
            </div>
            <ListBox title="Upgrade suggestions" items={payload.subscription.upgradeSuggestions} />
            <ListBox title="Warnings" items={payload.subscription.overageWarnings} />
          </div>
        </IntelligencePanel>

        {payload.clientJourney ? (
          <IntelligencePanel
            title="Client Journey"
            eyebrow="Lifecycle and delivery"
            description="A reusable client-facing timeline that links proposal acceptance, delivery, and archive workflows."
            icon={Users}
            className="xl:col-span-6"
          >
            <div className="grid gap-3">
              <div className="rounded-2xl bg-ktd-black p-4 text-white">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-ktd-softGold">Current stage</p>
                <p className="mt-2 font-display text-3xl font-bold">{payload.clientJourney.currentStage}</p>
                <p className="mt-2 text-sm font-semibold text-white/68">{payload.clientJourney.completionPercent}% complete</p>
              </div>
              <DataLine label="Proposal to project" value={payload.clientJourney.proposalToProjectRelationship} />
              <DataLine label="Delivery to archive" value={payload.clientJourney.deliveryToArchiveRelationship} />
              <DataLine label="Revision workflow" value={payload.clientJourney.revisionRequestWorkflow} />
              <DataLine label="Acceptance workflow" value={payload.clientJourney.acceptanceWorkflow} />
              <div className="grid gap-3 md:grid-cols-2">
                <ListBox title="Upcoming milestones" items={payload.clientJourney.upcomingMilestones} />
                <ListBox title="Deliverables" items={payload.clientJourney.deliverables} />
              </div>
              {payload.clientJourney.timeline.length ? (
                <div className="grid gap-3">
                  {payload.clientJourney.timeline.slice(0, 3).map((item) => (
                    <div key={item.id} className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{item.kind}</p>
                      <p className="mt-2 font-display text-2xl font-bold text-ktd-rich">{item.title}</p>
                      <p className="mt-2 text-sm font-semibold leading-6 text-ktd-muted">{item.detail}</p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </IntelligencePanel>
        ) : null}
      </section>
    </div>
  );
}

function WidgetChip({ widget }: { widget: BusinessCenterIntelligenceWidgetDescriptor }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-ktd-gold/15 bg-white px-3 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">
      <span className="size-2 rounded-full bg-[#FF6A2B]" />
      {widget.label}
    </span>
  );
}

function IntelligencePanel({
  title,
  eyebrow,
  description,
  icon: Icon,
  className,
  children,
}: {
  title: string;
  eyebrow: string;
  description: string;
  icon: typeof Sparkles;
  className?: string;
  children: ReactNode;
}) {
  return (
    <article className={`ivory-card rounded-[1.5rem] p-6 ${className || ""}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">{eyebrow}</p>
          <h3 className="mt-2 font-display text-3xl font-bold text-ktd-rich">{title}</h3>
          <p className="mt-3 text-sm font-semibold leading-7 text-ktd-muted">{description}</p>
        </div>
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-ktd-black text-ktd-softGold">
          <Icon className="size-5" />
        </span>
      </div>
      <div className="mt-6">{children}</div>
    </article>
  );
}

function RecommendationCard({ recommendation }: { recommendation: BusinessCenterRecommendationCard }) {
  return (
    <div className="rounded-2xl border border-ktd-gold/12 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{recommendation.category}</p>
          <h4 className="mt-2 font-display text-2xl font-bold text-ktd-rich">{recommendation.title}</h4>
        </div>
        <span className="rounded-full bg-[#FF6A2B]/20 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">
          {Math.round(recommendation.confidence * 100)}%
        </span>
      </div>
      <p className="mt-3 text-sm font-semibold leading-6 text-ktd-muted">{recommendation.summary}</p>
      <p className="mt-3 text-sm font-semibold leading-6 text-ktd-muted">{recommendation.explanation}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-ktd-black px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-ktd-softGold">
          {recommendation.estimatedImpact}
        </span>
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#D94A16]">
          {recommendation.estimatedTimeSavingsMinutes} min saved
        </span>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {recommendation.actionUrl ? (
          <Link
            to={recommendation.actionUrl}
            className="inline-flex items-center gap-2 rounded-full bg-ktd-black px-4 py-2 text-sm font-extrabold text-ktd-softGold"
          >
            {recommendation.actionLabel}
            <ArrowRight className="size-4" />
          </Link>
        ) : (
          <span className="rounded-full border border-ktd-gold/15 px-4 py-2 text-sm font-bold text-ktd-muted">
            {recommendation.actionLabel}
          </span>
        )}
        {recommendation.installed ? <span className="status-pill status-approved">Installed</span> : null}
      </div>
    </div>
  );
}

function MarketplaceGroup({
  label,
  cards,
}: {
  label: string;
  cards: BusinessCenterMarketplaceCard[];
}) {
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-ktd-muted">{cards.length} items</span>
      </div>
      <div className="mt-3 grid gap-3">
        {cards.length ? (
          cards.slice(0, 2).map((card) => (
            <div key={card.key} className="rounded-2xl bg-[#fbf7ef] p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">{card.category}</p>
                  <h4 className="mt-2 font-display text-2xl font-bold text-ktd-rich">{card.title}</h4>
                </div>
                <span className="rounded-full bg-ktd-black px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-ktd-softGold">
                  {card.status}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold leading-6 text-ktd-muted">{card.description}</p>
            </div>
          ))
        ) : (
          <div className="rounded-2xl border border-dashed border-ktd-gold/20 bg-[#fbf7ef] p-4 text-sm font-semibold leading-6 text-ktd-muted">
            No items are visible in this group yet.
          </div>
        )}
      </div>
    </div>
  );
}

function DataLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-ktd-muted">{value}</p>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-ktd-rich">{value}</p>
    </div>
  );
}

function ListBox({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{title}</p>
      <div className="mt-3 grid gap-2">
        {items.length ? (
          items.map((item) => (
            <div key={item} className="rounded-xl bg-[#fbf7ef] px-3 py-2 text-sm font-semibold leading-6 text-ktd-muted">
              {item}
            </div>
          ))
        ) : (
          <div className="rounded-xl bg-[#fbf7ef] px-3 py-2 text-sm font-semibold leading-6 text-ktd-muted">
            No items yet.
          </div>
        )}
      </div>
    </div>
  );
}

function StorageBar({ used, limit }: { used: number; limit: number }) {
  const percent = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <div className="flex items-center justify-between gap-4 text-sm font-bold text-ktd-muted">
        <span>{formatBytes(used)}</span>
        <span>{formatBytes(limit)}</span>
      </div>
      <div className="mt-3 h-3 overflow-hidden rounded-full bg-[#f4ead6]">
        <div className="h-full rounded-full bg-ktd-gold" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function HighlightCard({
  icon: Icon,
  label,
  value,
  note,
}: {
  icon: typeof Sparkles;
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
          <p className="mt-2 font-display text-2xl font-bold text-ktd-rich">{value}</p>
          <p className="mt-2 text-sm font-semibold leading-6 text-ktd-muted">{note}</p>
        </div>
        <span className="grid size-10 place-items-center rounded-2xl bg-ktd-black text-ktd-softGold">
          <Icon className="size-4" />
        </span>
      </div>
    </div>
  );
}

function MetricCard({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <div className="rounded-2xl border border-ktd-gold/10 bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
      <p className="mt-2 font-display text-4xl font-bold text-ktd-rich">{value}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-ktd-muted">{note}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-ktd-softGold">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-white/80">{value}</p>
    </div>
  );
}
